import PropTypes from 'prop-types';
import { isEqual, isEmpty, last } from 'underscore';
import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import { withTheme } from 'react-jss';
import { withChunkErrorActionGlobalErrorView } from '@bingads-webui-campaign-react/global-error-options';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { isSDK, isSDKPhase2Enabled } from '@bingads-webui-campaign-react/sdk-utils';
import { PMAX_LITE_WIZARD_STEP_PATHS, getFlow } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import {
  CampaignCreationIsolatedStep as NonSDKCampaignCreationIsolatedStep,
  SDKCampaignCreationIsolatedStep,
} from '@bingads-webui-campaign-react/pmax-campaign-creation-method';
import { PmaxCollectBusinessInfoIsolatedStep } from '@bingads-webui-campaign-react/pmax-collect-business-info';
import {
  usePreference,
  preferenceNames,
  UserCreationContextProvider,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import { useInitializeWizard, useWizardStepPreference } from '@bingads-webui-campaign-react/pmax-lite-wizard-hooks';
import { CouponProvider } from '@bingads-webui-campaign-react/pmax-coupon';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';

import { StepConfig } from './step-config/config';
import { DelayedSDKWizard } from './wizard/sdk-wizard';
import { DelayedPmaxLiteSignupWizard } from './wizard/pmax-lite-signup-wizard';
import { InlineSupport } from './support-components/inline-support';
import { SupportMenu } from './support-components/support-menu';
import { withAllDynamicFeatures } from './wizard/ad-extensions-dynamic';

const supportComponents = {
  inlineSupport: InlineSupport,
  supportMenu: SupportMenu,
};

const WizardComponent = isSDK() ? DelayedSDKWizard : DelayedPmaxLiteSignupWizard;
const CampaignCreationIsolatedStep = isSDK() ? SDKCampaignCreationIsolatedStep : NonSDKCampaignCreationIsolatedStep;

const PmaxLiteSignupWizard = (props) => {
  const {
    devConfig,
    adExtensionsDynamicFeaturesLoader,
    theme,
  } = props;

  const instrumentation = useContext(InstrumentationContext);
  const isCollectBusinessInfoEnabled = usePilotsValue('isPmaxLiteCollectBusinessInfoStepEnabled') && !isSDK();
  const isChinaSignup = usePilotsValue('isCNMarketExpansionEnabled');

  const activity = useMemo(() => {
    if (instrumentation && instrumentation.signupFlowScenario) {
      const scenario = instrumentation.signupFlowScenario.create();
      return scenario.signupFlow.create();
    }
    return null;
  }, [instrumentation]);

  const [currentWizardData, setCurrentWizardData] = useState();
  const [currentStepId, setCurrentStepId] = useState();
  const [currentFlowFactors, setCurrentFlowFactors] = useState();
  const [currentFlow, setCurrentFlow] = useState();

  const {
    isSmartPage, isCacheFlow, isPreviewFlow, isTargetingInsideChina,
  } = currentFlowFactors || {};
  const { websiteUrl, isShowingAnimation, loadWizardDirectly } = currentWizardData || {};

  const [{ value: websitePreference }] = usePreference(preferenceNames.WebsiteUrl);

  const currentInitialStep = isCollectBusinessInfoEnabled && !isChinaSignup
    ? PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO
    : PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD;

  const isInitialStep = (currentStepId === currentInitialStep) && !loadWizardDirectly;

  const isLastStep = currentFlow && (last(currentFlow) === currentStepId);
  const paymentStep = isPreviewFlow ? PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_PAYMENT : PMAX_LITE_WIZARD_STEP_PATHS.PAYMENT;
  const isInOrPostPaymentStep = currentFlow &&
    (currentFlow.indexOf(currentStepId) >= currentFlow.indexOf(paymentStep));
  const isSkipWebsiteValidation = isSmartPage || !websiteUrl;

  const [stepPreference, updateStepPreference] = useWizardStepPreference();
  const {
    isInitialized,
    initialStep,
    initialFlowFactors,
    initialWizardData,
  } = useInitializeWizard(stepPreference, adExtensionsDynamicFeaturesLoader, theme);

  useEffect(() => {
    if (isPreviewFlow && activity) {
      activity.trace('[SignupFlow] User is in preview flow.');
    }
  }, [activity, isPreviewFlow]);

  useEffect(() => {
    if (isInitialized) {
      setCurrentFlowFactors(initialFlowFactors);
      setCurrentWizardData({
        websiteUrl: websitePreference,
        websites: [websitePreference],
        ...initialWizardData,
      });
      setCurrentFlow(getFlow(initialFlowFactors));
      setCurrentStepId(initialStep);
      activity?.trace(`[SignupFlow] Wizard initialized with flow factors: ${JSON.stringify(initialFlowFactors)}, wizard data: ${JSON.stringify(initialWizardData)}`);
    }
  }, [activity, initialFlowFactors, initialStep, initialWizardData, isInitialized, websitePreference]);

  const observer = useCallback((state) => {
    const { flowFactors, currentFlow: currentFlowInContext } = state.context;
    const stepId = state.toStrings()[0];
    setCurrentStepId(stepId);
    setCurrentFlowFactors((previousFlowFactors) => {
      if (isEqual(flowFactors, previousFlowFactors)) {
        return previousFlowFactors;
      }
      return flowFactors;
    });
    setCurrentFlow((previousFlow) => {
      if (isEqual(currentFlowInContext, previousFlow)) {
        return previousFlow;
      }
      return currentFlowInContext;
    });
  }, []);

  const onStepSelection = useCallback((stepId, flowFactors, newWizardData) => {
    setCurrentStepId(stepId);
    setCurrentFlowFactors(previousFlowFactors => ({
      ...previousFlowFactors,
      ...flowFactors,
    }));
    if (!isEmpty(newWizardData)) {
      setCurrentWizardData(previousWizardData => ({
        ...previousWizardData,
        ...newWizardData,
      }));
    }
  }, []);

  const onCollectBusinessInfoStepSelection = useCallback((stepId, flowFactors, newWizardData) => {
    onStepSelection(stepId, {
      ...flowFactors,
      isGoalBasedSignup: true,
    }, newWizardData);
  }, [onStepSelection]);

  const setIsTargetingInsideChina = useCallback((value) => {
    setCurrentFlowFactors(previousFlowFactors => ({
      ...previousFlowFactors,
      isTargetingInsideChina: value,
    }));
  }, []);

  const onRenderIsolatedStep = useCallback(() => {
    if (isCollectBusinessInfoEnabled && !isChinaSignup) {
      return (
        <PmaxCollectBusinessInfoIsolatedStep
          onStepSelection={onCollectBusinessInfoStepSelection}
          supportComponents={supportComponents}
          currentFlow={currentFlow}
          initialWebsiteUrl={isCacheFlow ? websiteUrl : ''}
          loadingAtBeginningEnabled={!!isCacheFlow}
          isTargetingInsideChina={isTargetingInsideChina}
          setIsTargetingInsideChina={setIsTargetingInsideChina}
        />
      );
    }

    return (
      <CampaignCreationIsolatedStep
        onStepSelection={onStepSelection}
        supportComponents={supportComponents}
        currentFlow={currentFlow}
      />
    );
  }, [currentFlow, isCacheFlow, isChinaSignup, isCollectBusinessInfoEnabled, isTargetingInsideChina,
    onCollectBusinessInfoStepSelection, onStepSelection, setIsTargetingInsideChina, websiteUrl]);

  useEffect(() => {
    import('@bingads-webui-campaign-react/pmax-lite-wizard');
  }, []);

  if (!currentStepId) {
    return null;
  }

  return (
    <UserCreationContextProvider devConfig={devConfig} isCacheFlow={!!isCacheFlow}>
      <CouponProvider
        isSkipWebsiteValidation={isSkipWebsiteValidation}
        shouldHidePromotionBanner={isInOrPostPaymentStep || isShowingAnimation || loadWizardDirectly}
        isLastStep={isLastStep}
        website={websiteUrl}
      >
        {isInitialStep && onRenderIsolatedStep()}
        {!isInitialStep && (
          <WizardComponent
            devConfig={devConfig}
            stepConfig={StepConfig}
            supportComponents={supportComponents}
            observer={observer}
            stepPreference={stepPreference}
            updateStepPreference={updateStepPreference}
            isInitialized={isInitialized}
            initialStep={currentStepId}
            initialFlowFactors={currentFlowFactors}
            initialWizardData={currentWizardData}
          />
        )}
      </CouponProvider>
    </UserCreationContextProvider>
  );
};

PmaxLiteSignupWizard.propTypes = {
  devConfig: PropTypes.shape({
    isInDevelopmentMode: PropTypes.bool.isRequired,
  }).isRequired,
  adExtensionsDynamicFeaturesLoader: PropTypes.func.isRequired,
  theme: PropTypes.shape({}).isRequired,
};

const PmaxLiteSignupWizardWithDynamicFeatures = withAllDynamicFeatures(withTheme(PmaxLiteSignupWizard));

export const PmaxLiteSignupAppCoreExt = withChunkErrorActionGlobalErrorView(({ devConfig }) => {
  if (isSDK() && !isSDKPhase2Enabled()) {
    return (
      <DelayedSDKWizard
        devConfig={devConfig}
        stepConfig={StepConfig}
        supportComponents={supportComponents}
      />
    );
  }

  return (
    <PmaxLiteSignupWizardWithDynamicFeatures
      devConfig={devConfig}
    />
  );
});

PmaxLiteSignupAppCoreExt.propTypes = {
  devConfig: PropTypes.shape({
    isInDevelopmentMode: PropTypes.bool.isRequired,
  }).isRequired,
};
